import ApplicationController from "../../application_component_controller";

import { useIntersection } from "stimulus-use";
import { useQuery } from "hb/insights/use_query";
import { useEditing } from "hb/insights/use_editing";
import { useInlineEditing } from "hb/insights/use_inline_editing";
import { useOutlets } from "hb/insights/use_outlets";
import { useListen } from "hb/insights/use_listen";
import { call } from "hb/insights/use";
import { userTZ } from "hb/lib/date_helpers";
import { DEFAULT_TS_PARAM } from "hb/insights/globals";
import { fetchTurbo } from "hb/lib/fetch";
import { deepEqual } from "hb/lib/utils";
import { duplicate } from "hb/insights/utils";

import URL from "hb/lib/url";
import Query from "hb/insights/query";
import { resolveParameters, hasUnresolvedParameters, extractParameters } from "hb/insights/query_parameters";

export default class extends ApplicationController {
  static viewId = "grid-item";
  static targets = [
    "showQueryLink",
    "title",
    "subtitle",
    "loader",
    "error",
    "errorTemplate",
  ];

  static values = {
    querySessionId: String,
    queryPath: String,
    defaultTs: String,
    widget: Object,
    widgetType: String,
    viewPath: String,
    widgetPath: String,
    queryConfig: Object,
    widgetId: String,
    widgetConfig: Object,
    dashboardId: String,
    streamIds: Array,
    editing: Boolean,
    editOnCreate: Boolean,
    storedWidget: Object,
    parametersEnabled: { type: Boolean, default: false },
  };

  connect() {
    this.errorTemplate = this.compileElement(this.errorTemplateTarget);

    // We scope the outlet search to this element as the vis class can bleed
    // into other grid items.
    useOutlets(this, {
      components: ["vis", "ballBounceLoader"],
      scope: this.element,
    });
    useListen(this);

    this.showQueryLink = this.find(".external-insights-link");

    this.didAppear = false;
    this.listenWindow("urlchanged", this.urlChanged);
    this.listenWindow("dashboard:refresh", this.refresh);
    this.listenWindow(
      "dashboard:updateProductWidgetConfig",
      this.handleProductConfigUpdate,
    );
    this.listenWindow("dashboard:editWidget", this.handleEditWidget);
    this.listenWindow(
      "dashboard:finishEditingWidget",
      this.handleFinishEditingWidget,
    );

    this.storedWidget = this.hasStoredWidgetValue
      ? this.storedWidgetValue
      : this.widgetValue || {};
    this.storedWidget.presentation = this.storedWidget.presentation || {};
    this.localWidget = this.widgetValue || {};
    this.localWidget.presentation = this.localWidget.presentation || {};
    this.lastResolvedQuery = null;

    useEditing(this);
    useInlineEditing(this, { targets: ["title", "subtitle"] });
    this.deleted = false;

    this.titleTarget.addEventListener("input", () => {
      this.localWidget.presentation.title = this.titleTarget.textContent.slice(0, 255).trim();
      this.checkForChanges();
    });
    this.subtitleTarget.addEventListener("input", () => {
      this.localWidget.presentation.subtitle =
        this.subtitleTarget.textContent.slice(0, 255).trim();
      this.checkForChanges();
    });

    this.updateShowQueryLink();

    // If editing flag is set, enter edit mode immediately
    this.resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        if (this.element.offsetWidth < 320) {
          this.element.classList.add("squished");
        } else {
          this.element.classList.remove("squished");
        }
      });
    });

    this.resizeObserver.observe(this.element);
  }

  outletsConnected() {
    if (this.hasQuerySessionIdValue) {
      useQuery(this, { sessionId: this.querySessionIdValue });
    }

    useIntersection(this);

    if (this.editingValue) {
      this.edit();
    }

    if (this.editOnCreateValue) {
      this.editWidget();
    }
  }

  disconnect() {
    this.resizeObserver.disconnect();
  }

  get id() {
    return this.element.getAttribute("gs-id");
  }

  get gridNode() {
    return this.element.gridstackNode;
  }

  get grid() {
    if (this._grid) return this._grid;
    const gridElement = this.element.closest(".grid-stack");
    return gridElement ? gridElement.gridstack : null;
  }

  get gridDims() {
    return ["x", "y", "h", "w"];
  }

  get gridPosition() {
    const pos = {};
    this.gridDims.forEach((d) => {
      pos[d] = parseInt(this.element.getAttribute(`gs-${d}`));
    });
    return pos;
  }

  get query() {
    return this.element.dataset.query;
  }

  refresh() {
    if (this.didAppear) {
      if (this.widgetTypeValue === "insights_vis") {
        this.search();
      } else {
        this.refreshProductWidget();
      }
    } else {
      this.didAppear = false;
    }
  }

  refreshProductWidget() {
    this.element.classList.add("loading");
    this.startLoader();
    const currentGridPos = this.gridPosition;
    // We have to embed the current state of the widget so it will re-render properly
    this.emitWindow("dashboard:refreshWidget", {
      ...this.localWidget,
      // If currentGridPos.w is NaN (can happen with small screens), use stored grid position
      grid: Number.isNaN(currentGridPos.w)
        ? this.localWidget.grid
        : currentGridPos,
      editing: this.element.classList.contains("editing"),
      widget_editing: this.element.classList.contains("widget-editing"),
      has_changes: this.element.classList.contains("has-changes"),
      original_widget: this.storedWidget,
    });
  }

  edit() {
    this.prevProps = {};
    this.gridDims.forEach((d) => {
      this.prevProps[d] = parseInt(this.element.getAttribute(`gs-${d}`));
    });

    // If gridNode doesn't exist (after Turbo Stream), re-add element to grid at existing position
    // But don't re-add deleted widgets
    if (!this.element.gridstackNode && this.grid && !this.deleted) {
      // First, remove any existing widget with the same ID to avoid duplicates
      const existingNode = this.grid.engine.nodes.find(
        (node) => node.el.getAttribute("gs-id") === this.id,
      );
      if (existingNode) {
        this.grid.removeWidget(existingNode.el, false, false);
      }

      const position = {
        x: this.prevProps.x,
        y: this.prevProps.y,
        w: this.prevProps.w,
        h: this.prevProps.h,
      };
      this.grid.addWidget(this.element, position);
    }

    // Now set the grid node properties
    if (this.element.gridstackNode) {
      this.gridDims.forEach((d) => {
        this.element.gridstackNode[d] = this.prevProps[d];
      });
    }

    return this.widgetValue;
  }

  cancel() {
    if (this.deleted) {
      this.element.classList.remove("deleted");
      this.element.classList.add("grid-stack-item");
      this.grid.addWidget(this.element);
      this.deleted = false;
    }

    this.element.classList.remove("widget-editing");
    this.element.classList.remove("has-changes");
    return this.prevProps;
  }

  checkAndDelete() {
    if (this.deleted) {
      this.element.remove();
      return true;
    }

    return false;
  }

  save(gridNode) {
    this.element.classList.remove("widget-editing");
    this.element.classList.remove("has-changes");

    this.localWidget.presentation.title = this.titleTarget.textContent.slice(0, 255).trim();
    this.localWidget.presentation.subtitle =
      this.subtitleTarget.textContent.slice(0, 255).trim();
    this.localWidget.grid = {
      x: gridNode.x,
      y: gridNode.y,
      w: gridNode.w,
      h: gridNode.h,
    };

    this.storedWidget = duplicate(this.localWidget);
    return this.localWidget;
  }

  gridLoaded() {
    this._grid = this.gridNode.grid;
  }

  updateShowQueryLink() {
    if (this.localWidget.type !== "insights_vis") return;

    let config = this.localWidget.config || {};
    const query = config.query || "";

    const params = URL.slice(["ts", "timezone"]);
    params.set("query", query);

    // Carry over current parameter values so the query page can resolve them
    if (this.parametersEnabledValue) {
      const queryParams = extractParameters(query);
      const urlParams = new URLSearchParams(window.location.search);
      queryParams.forEach((p) => {
        const value = urlParams.get(p.name);
        if (value !== null && value !== "") {
          params.set(p.name, value);
        }
      });
    }
    params.set("view", config.vis?.view || "table");
    if (config.stream_ids != null) {
      params.set("stream_ids", JSON.stringify(config.stream_ids));
    }

    if (config.vis?.chart_config !== undefined) {
      URL.mergeParams(config.vis.chart_config, params);
    }

    if (config.vis?.chart_config?.groups !== undefined) {
      params.set("groups", JSON.stringify(config.vis.chart_config.groups));
    }
    if (this.hasDashboardIdValue) {
      params.set("dashboard", this.dashboardIdValue);
      params.set("widget", this.id);
    }

    this.showQueryLink.href = `${this.queryPathValue}?${params}`;
  }

  editWidget() {
    this.element.classList.add("widget-editing");
    this.emitWindow("dashboard:editWidget", {
      querySessionId: this.querySessionIdValue,
      widget: this.localWidget,
    });
  }

  handleEditWidget(event) {
    if (event.widget.id !== this.id) {
      this.element.classList.remove("widget-editing");
    }
  }

  handleFinishEditingWidget({ widgetId }) {
    if (widgetId === this.id) {
      this.element.classList.remove("widget-editing");
    }
  }

  removeWidget() {
    this.deleted = true;
    this.grid.removeWidget(this.element, false, false);
    this.element.classList.add("deleted");
  }

  deleteWidget() {
    this.grid.removeWidget(this.element, false, false);
  }

  onQueryStart() {
    this.element.classList.add("loading");
    this.element.classList.remove("query-error");
    this.startLoader();
  }

  onQueryError({
    detail: {
      query: { data },
    },
  }) {
    const { error } = data.response;
    this.stopLoader();

    this.errorTarget.innerHTML = this.errorTemplate({
      error: error.message,
      queryLink: this.showQueryLink?.href,
    });

    this.element.classList.remove("loading");
    this.element.classList.add("query-error");
  }

  startLoader() {
    this.outletComponent("ballBounceLoader").start();
  }

  stopLoader() {
    this.outletComponent("ballBounceLoader").stop();
  }

  onQueryReady() {
    this.element.classList.remove("loading");
    this.stopLoader();
    setTimeout(() => call(this.outletComponent("vis"), "dashboardRender"), 10);
  }

  appear() {
    if (this.didAppear !== true) {
      this.search();
    }
    this.didAppear = true;
  }

  urlChanged() {
    const params = new URLSearchParams(window.location.search);
    const urlTz = params.get("timezone") || userTZ();
    const urlTs = params.get("ts") || this.defaultTsValue || DEFAULT_TS_PARAM;
    const updatedTz = this.timezone !== urlTz;
    const updatedTs = this.ts !== undefined && this.ts !== urlTs;

    // For insights_vis widgets, also check if any query parameters changed
    const hasParameterChange =
      this.parametersEnabledValue &&
      this.widgetTypeValue === "insights_vis" &&
      this.queryParametersChanged();

    if (this.didAppear && (updatedTs || updatedTz || hasParameterChange)) {
      if (this.widgetTypeValue === "insights_vis") {
        this.search();
      } else if (
        call(
          this.outletComponent("vis"),
          "shouldReload",
          this.localWidget.config,
        )
      ) {
        this.refreshProductWidget();
      }
    }

    this.updateShowQueryLink();
  }

  queryParametersChanged() {
    const rawQuery = this.localWidget.config?.query || "";
    if (extractParameters(rawQuery).length === 0) {
      return false;
    }

    if (this.element.classList.contains("parameters-required")) {
      return true;
    }

    const resolvedQuery = resolveParameters(rawQuery);
    if (this.lastResolvedQuery === null) {
      // First evaluation: initialize lastResolvedQuery without triggering a refresh
      this.lastResolvedQuery = resolvedQuery;
      return false;
    }

    return this.lastResolvedQuery !== resolvedQuery;
  }

  handleProductConfigUpdate({ widgetId, field, value }) {
    if (widgetId !== this.id) return;
    const localValue = this.localWidget.config[field];
    if (localValue === value) return;
    this.localWidget.config[field] = value;
    this.checkForChanges();
    this.refreshProductWidget();
  }

  checkForChanges() {
    if (!this.storedWidget) return;

    const hasConfigChanges = !deepEqual(
      this.localWidget.config,
      this.storedWidget.config,
    );
    const hasPresentationChanges = !deepEqual(
      this.localWidget.presentation,
      this.storedWidget.presentation,
    );

    if (hasConfigChanges || hasPresentationChanges) {
      this.element.classList.add("has-changes");
    } else {
      this.element.classList.remove("has-changes");
    }
  }

  refreshWidget() {
    if (this.widgetTypeValue === "insights_vis") {
      this.search();
    } else {
      this.refreshProductWidget();
    }
  }

  resetConfig(config) {
    this.localWidget.config = config;
  }

  setConfig(config) {
    this.localWidget.config = config;
    this.checkForChanges();
    this.refreshWidget();
  }

  async updateQueryVisConfig(config) {
    let needsRefresh = false;

    if (config.query !== this.localWidget.config.query) {
      needsRefresh = true;
    }

    if (config.stream_ids !== this.localWidget.config.stream_ids) {
      needsRefresh = true;
    }

    if (
      config.vis.view &&
      config.vis.view !== this.localWidget.config.vis.view
    ) {
      await fetchTurbo({
        path: this.viewPathValue,
        method: "GET",
        params: {
          view: config.vis.view,
          querySessionId: this.querySessionIdValue,
          selector: `widget-vis-${this.widgetIdValue}`,
          hideConfig: true,
          chart_config: JSON.stringify(config.vis.chart_config || {}),
          use_param_state: false,
        },
      });
    } else if (
      !deepEqual(
        config.vis.chart_config,
        this.localWidget.config.vis.chart_config,
      )
    ) {
      const vis = this.outletComponent("vis");
      vis.config = config.vis.chart_config || {};
      call(vis, "setConfig", config.vis.chart_config);
      call(vis, "processConfig");
      call(vis, "_render");
    }

    this.localWidget.config = config;
    this.updateShowQueryLink();
    this.checkForChanges();

    if (needsRefresh && this.didAppear) {
      this.search();
    }
  }

  async search() {
    if (!this.hasQueryPathValue) {
      return;
    }

    const rawQuery = this.localWidget.config.query || "";

    if (this.parametersEnabledValue) {
      // Expose raw query for parameters modal to read
      this.element.dataset.insightsDashboardGridItemQueryValue = rawQuery;

      // Check for unresolved parameters
      if (hasUnresolvedParameters(rawQuery)) {
        this.showParametersRequired();
        return;
      }
    }

    // Substitute parameters before executing query
    const resolvedQuery = this.parametersEnabledValue
      ? resolveParameters(rawQuery)
      : rawQuery;

    this.element.setAttribute("data-query", resolvedQuery);
    this.element.classList.remove("parameters-required");
    this.lastResolvedQuery = resolvedQuery;

    const params = URL.slice(["ts", "timezone"]);
    this.ts = params.get("ts") || this.defaultTsValue || DEFAULT_TS_PARAM;
    this.timezone = params.get("timezone") || userTZ();

    let args = {
      ts: this.ts,
      timezone: this.timezone,
      query: resolvedQuery,
      querySessionId: this.querySessionIdValue,
      path: this.queryPathValue,
      source: "dashboards",
    };

    if (this.localWidget.config.stream_ids != null) {
      args.streamIds = this.localWidget.config.stream_ids;
    }

    await Query.fetch(args);
  }

  showParametersRequired() {
    this.stopLoader();
    this.element.classList.remove("loading");
    this.element.classList.add("parameters-required");
  }
}
